import Moyasar from 'moyasar-payment-form';
import { useEffect, useMemo, useRef, useState } from 'react';

import { BACKEND_BASE_URL, client } from '@workspace/ui';

export interface MoyasarFormProps {
  /** Halalas. Must equal the course price the API will verify against. */
  amount: number;
  description: string;
  /** Serialised into Moyasar's metadata so the API can rebuild the checkout. */
  metadata: { courseSlug: string; uid: string };
  /** Called when the pre-charge check finds the rendered amount out of date. */
  onAmountStale?: () => void;
}

/**
 * Renders Moyasar's hosted card form.
 *
 * The form charges the card directly with the publishable key and then sends
 * the browser to the API's callback. Nothing here decides whether the payment
 * succeeded — the API re-reads it from Moyasar before granting the course.
 *
 * The SDK is a locked dependency rather than a CDN script so the bytes that
 * render the card fields are the ones the lockfile hashes, served from our own
 * origin. A third-party CDN here would put card entry one unpkg compromise away
 * from a skimmer. Its stylesheet is imported from `styles.css`, which explains
 * the cascade order it needs.
 */
export function MoyasarForm({
  amount,
  description,
  metadata,
  onAmountStale,
}: MoyasarFormProps) {
  const container = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);

  const onAmountStaleRef = useRef(onAmountStale);

  useEffect(() => {
    onAmountStaleRef.current = onAmountStale;
  });

  // Moyasar metadata values must be strings, so the whole object travels as one
  // JSON blob. Memoising on the serialised form keeps the effect from
  // re-initialising the form on every render.
  const transferables = useMemo(() => JSON.stringify(metadata), [metadata]);

  useEffect(() => {
    const host = container.current;

    if (!host) {
      return;
    }

    // Moyasar renders with Preact, which keeps its bookkeeping properties on
    // the element it rendered into. Wiping innerHTML leaves that bookkeeping
    // behind, so the next init diffs against a tree that claims the DOM still
    // exists and renders nothing — which is how StrictMode's dev remount left
    // the form empty. A fresh element per init makes every render a first
    // render.
    const element = document.createElement('div');
    element.className = 'mysr-form';
    host.replaceChildren(element);

    // Deliberately uncaught: if the form cannot initialise there is nothing to
    // pay with, so the route's ErrorBoundary should take over the page rather
    // than leaving an empty card behind.
    Moyasar.init({
      element,
      language: 'en',
      amount,
      currency: 'SAR',
      description,
      publishable_api_key: import.meta.env.VITE_MOYASAR_PUBLISHABLE_KEY,
      callback_url: `${BACKEND_BASE_URL}/payments/callback`,
      metadata: { transferables },
      // Listing `applepay` obliges us to pass `apple_pay` below — Moyasar
      // validates the pair and renders nothing at all if it is missing, even in
      // browsers that could never show the button. `stcpay` needs no config.
      methods: ['creditcard', 'applepay', 'stcpay'],
      supported_networks: ['visa', 'mastercard', 'mada'],
      // Moyasar performs the merchant validation itself at this URL, but Apple
      // still requires the checkout domain to be registered against the
      // merchant ID before the sheet will open.
      apple_pay: {
        country: 'SA',
        label: 'ITGL Academy',
        validate_merchant_url: 'https://api.moyasar.com/v1/applepay/initiate',
      },
      // Must stay `async`: Moyasar validates every callback by checking for an
      // AsyncFunction, and a plain arrow makes it reject the whole config and
      // render its own "Form configuration issue!" card instead of the form.
      //
      // Charging a stale amount strands the money: the API rejects any charge
      // that no longer matches the course price, with no recovery path.
      // Re-reading the course right before the charge turns that into a
      // blocked click and a refreshed price instead.
      on_initiating: async () => {
        try {
          const fresh = await client.request('GET /courses/{slug}', {
            slug: metadata.courseSlug,
          });

          if (fresh.price === amount && !fresh.comingSoon) {
            return true;
          }

          setError(
            'The course has changed since this page loaded. Please review the updated details before paying.',
          );
        } catch {
          setError('We could not confirm the course price. Please try again.');
        }

        onAmountStaleRef.current?.();

        return false;
      },
      on_failure: async (reason: unknown) => {
        console.error('Moyasar payment failed', reason);
        setError('That payment did not go through. Please try again.');
      },
    });

    return () => {
      element.remove();
    };
  }, [amount, description, transferables, metadata.courseSlug]);

  return (
    <div className="space-y-3">
      {error && <p className="text-destructive text-sm">{error}</p>}
      <div ref={container} />
    </div>
  );
}
